import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { v4 as uuidv4 } from "uuid";
import { useSelector } from "react-redux";
import { Button } from "@/components/ui/button";
import { TreatmentPlanModal } from "@/components/PatientConsultation/components/TreatmentPlan/TreatmentPlanModal";
import {
  TravelHealthTier1Assessment,
  type Tier1AssessmentData,
  type Tier1SavedState,
} from "./TravelHealthTier1Assessment";
import {
  TravelHealthTier1Selection,
  type Tier1SelectionSavedState,
} from "./TravelHealthTier1Selection";
import {
  TravelHealthTier2Runner,
  type Tier2SavedState,
} from "./TravelHealthTier2Runner";
import { TravelHealthPlanTimeline } from "./TravelHealthPlanTimeline";
import type { AppointmentSlot } from "@/lib/travelHealth/scheduling";
import type { TreatmentFollowUpAppointment } from "@/lib/treatmentPlan/treatmentPlanData";
import { initialMembers } from "@/components/Calendar/membersData";
import { useLocationsSettingsData } from "@/lib/settings/locationsData";
import { calculatePatientAgeYears } from "@/lib/utils/patientAge";
import type { TravelHealthSummaryData } from "./TravelHealthConsultationSummary";
import useTitanApp from "@/hooks/useTitanApp";

import type {
  TravelHealthSnapshot,
  Tier1Snapshot,
  Tier1SelectionSnapshot,
  Tier2Snapshot,
  TravelHealthPhase,
} from "@/lib/travelHealth/saveResume";
import { useTravelHealthAutoSave } from "@/lib/travelHealth/saveResume";
import {
  updateConsultationTemplate,
  updateConsultationActions,
  SaveAIChatMessage,
  setAddPatientAIChatMessage,
} from "@/dispatchers/patientDispatchers";

interface TravelHealthConsultationProps {
  patient: any;
  activeSiteId: string | null;
  isYellowFeverCentre: boolean;
  yellowFeverCentreNumber: string;
  isDarkMode: boolean;
  schedulePharmacists: string[];
  scheduleRoomNames: string[];
  consultationSummary?: string;
  consultationId?: string;
  companyId?: string;
  hasExistingTemplate?: boolean;
  savedSnapshot?: TravelHealthSnapshot | null;
  onClose: (summaryData?: TravelHealthSummaryData) => void;
}

function convertSlotsToFollowUpAppointments(
  slots: AppointmentSlot[],
  memberNameMap: Record<string, string>,
): TreatmentFollowUpAppointment[] {
  return slots.map((slot, index) => {
    const slotAny = slot as any;

    const pharmacistName =
      slotAny.selectedPharmacistName ||
      memberNameMap[slot.availablePharmacistIds[0] ?? ""] ||
      "";
    const pharmacistId =
      slotAny.selectedPharmacistId ||
      slot.availablePharmacistIds[0] ||
      "";
    const serviceName = slotAny.selectedServiceName || "Travel Health";
    const serviceId = slotAny.selectedServiceId || "";
    const time = slotAny.selectedTime || "09:00";

    const notes: string[] = [];
    if (slot.noQualifiedPharmacistWarning) {
      notes.push(
        "No Travel Health qualified pharmacist available on this date — reassign required.",
      );
    }

    return {
      id: uuidv4(),
      serviceType: serviceName,
      serviceId,
      date: slot.date,
      time,
      pharmacist: pharmacistName,
      resourceId: pharmacistId,
      duration: "30",
      room: "",
      notes: notes.join(" "),
      tasksToComplete: [],
      vaccinesAdminister: slot.doses.map((d) => ({
        id: uuidv4(),
        name: d.productName,
        vaccineName: d.productName,
        dose: d.doseLabel,
        notes: d.isAfterDeparture ? "⚠ After departure date" : "",
      })),
    };
  });
}

export function TravelHealthConsultation({
  patient,
  activeSiteId,
  isYellowFeverCentre,
  yellowFeverCentreNumber,
  isDarkMode,
  schedulePharmacists,
  scheduleRoomNames,
  consultationSummary,
  consultationId,
  companyId,
  hasExistingTemplate = false,
  savedSnapshot,
  onClose,
}: TravelHealthConsultationProps) {
  const initialPhase = savedSnapshot?.phase;
  const initialTier2 = savedSnapshot?.tier2;

  const [travelHealthStep, setTravelHealthStep] = useState<
    "plan-check" | "tier1-assessment" | "tier1-selection" | "tier2-runner"
  >(() => {
    if (initialTier2) return "tier2-runner";
    if (initialPhase === "tier1-selection" && savedSnapshot?.tier1Selection) return "tier1-selection";
    if (initialPhase === "tier1-assessment" && savedSnapshot?.tier1) return "tier1-assessment";
    return "plan-check";
  });
  const [tier1AssessmentData, setTier1AssessmentData] =
    useState<Tier1AssessmentData | null>(() => {
      if (
        savedSnapshot?.tier1?.data &&
        (savedSnapshot.phase === "tier1-selection" || savedSnapshot.phase === "tier2-runner")
      ) {
        return savedSnapshot.tier1.data as Tier1AssessmentData;
      }
      return null;
    });
  const [travelHealthPlanId, setTravelHealthPlanId] = useState<string | null>(
    () => initialTier2?.planId ?? null,
  );
  const [travelHealthSitesUsedToday, setTravelHealthSitesUsedToday] =
    useState<{ site: string; productName: string }[]>([]);

  useEffect(() => {
    setTravelHealthSitesUsedToday([]);
  }, [travelHealthPlanId]);
  const handleTravelHealthSiteRecorded = (site: string, productName: string) => {
    setTravelHealthSitesUsedToday((prev) => [...prev, { site, productName }]);
  };
  const [travelHealthProductId, setTravelHealthProductId] = useState<
    string | null
  >(() => initialTier2?.productId ?? null);
  const [travelHealthPgdId, setTravelHealthPgdId] = useState<string | null>(
    () => initialTier2?.pgdId ?? null,
  );
  const [travelHealthAppointmentSlots, setTravelHealthAppointmentSlots] =
    useState<AppointmentSlot[]>([]);
  const [showTravelHealthPlanModal, setShowTravelHealthPlanModal] =
    useState(false);
  const [showTravelHealthPlanTimeline, setShowTravelHealthPlanTimeline] =
    useState(false);

  // ── Auto-save plumbing ──────────────────────────────────────────────────
  const tier1SnapshotRef = useRef<Tier1Snapshot | null>(
    savedSnapshot?.tier1 ?? null,
  );
  const tier1SelectionSnapshotRef = useRef<Tier1SelectionSnapshot | null>(
    savedSnapshot?.tier1Selection ?? null,
  );
  const tier2SnapshotRef = useRef<Tier2Snapshot | null>(
    savedSnapshot?.tier2
      ? {
          planId: savedSnapshot.tier2.planId,
          productId: savedSnapshot.tier2.productId,
          pgdId: savedSnapshot.tier2.pgdId,
          ...savedSnapshot.tier2,
        }
      : null,
  );
  const [autoSaveTrigger, setAutoSaveTrigger] = useState(0);

  const handleTier1AutoSave = useCallback((snapshot: Tier1SavedState) => {
    tier1SnapshotRef.current = snapshot as Tier1Snapshot;
    setAutoSaveTrigger((n) => n + 1);
  }, []);

  const handleTier1SelectionAutoSave = useCallback(
    (snapshot: Tier1SelectionSavedState) => {
      tier1SelectionSnapshotRef.current = snapshot as Tier1SelectionSnapshot;
      setAutoSaveTrigger((n) => n + 1);
    },
    [],
  );

  const handleTier2AutoSave = useCallback(
    (snapshot: Tier2SavedState) => {
      tier2SnapshotRef.current = {
        ...snapshot,
        planId: travelHealthPlanId ?? "",
        productId: travelHealthProductId ?? "",
        pgdId: travelHealthPgdId ?? "",
      } as Tier2Snapshot;
      setAutoSaveTrigger((n) => n + 1);
    },
    [travelHealthPlanId, travelHealthProductId, travelHealthPgdId],
  );

  const autoSavePhase: TravelHealthPhase =
    travelHealthStep === "tier2-runner"
      ? "tier2-runner"
      : travelHealthStep === "tier1-selection"
        ? "tier1-selection"
        : "tier1-assessment";

  useTravelHealthAutoSave({
    consultationId: consultationId ?? "",
    companyId: companyId ?? "",
    hasExistingTemplate,
    phase: autoSavePhase,
    tier1Snapshot: tier1SnapshotRef.current,
    tier1SelectionSnapshot:
      travelHealthStep === "tier1-selection" ? tier1SelectionSnapshotRef.current : null,
    tier2Snapshot: travelHealthStep === "tier2-runner" ? tier2SnapshotRef.current : null,
  });

  const clearSavedState = useCallback(async () => {
    if (!consultationId) return;
    try {
      await updateConsultationTemplate({
        consultationId,
        payload: [],
      });
    } catch (err) {
      console.error("[TravelHealth] failed to clear saved state:", err);
    }
  }, [consultationId]);

  const { initialLocations } = useLocationsSettingsData();
  const { patientState } = useTitanApp();
  const { patientVitals } = patientState || {};

  const vitalsWeightKg = useMemo(() => {
    const v = patientVitals?.weight?.value;
    if (v == null) return undefined;
    const n = typeof v === "string" ? parseFloat(v) : v;
    return Number.isNaN(n) ? undefined : String(n);
  }, [patientVitals?.weight?.value]);

  const isConsultationCompleted = useMemo(() => {
    if (!consultationSummary) return false;
    try {
      let parsed = JSON.parse(consultationSummary);
      if (typeof parsed === "string") parsed = JSON.parse(parsed);
      return parsed?._type === "travel-health-consultation";
    } catch {
      return false;
    }
  }, [consultationSummary]);

  // Derived age for the Travel Health flow.
  const travelHealthPatientAge =
    calculatePatientAgeYears(patient.dateOfBirth) ?? 35;

  const travelHealthQuestionnaireResponses = useMemo(() => {
    return undefined;
  }, [(patient as any).id, (patient as any).nhsNumber]);

  const travelHealthPlanName: string = (() => {
    if (!tier1AssessmentData) return "Travel Health Plan";
    const destinations =
      tier1AssessmentData.tripDestinations.length > 0
        ? tier1AssessmentData.tripDestinations.join(", ")
        : "Travel";
    const depDate = tier1AssessmentData.departureDate
      ? new Date(
          tier1AssessmentData.departureDate + "T00:00:00",
        ).toLocaleDateString("en-GB", {
          day: "numeric",
          month: "short",
          year: "numeric",
        })
      : "";
    return depDate
      ? "Travel Health \u2014 " + destinations + " \u2014 " + depDate
      : "Travel Health \u2014 " + destinations;
  })();

  const travelHealthExistingPlans = useSelector((state: any) =>
    state.travelHealth.plans.filter(
      (p: any) =>
        p.status === "active" &&
        p.patientId ===
          ((patient as any).id ||
            (patient as any).nhsNumber ||
            "current-patient"),
    ),
  );

  const allPlans = useSelector((state: any) => state.travelHealth.plans);
  const allVisits = useSelector((state: any) => state.travelHealth.visits);
  const allDoseAuditRecords = useSelector(
    (state: any) => state.travelHealth.doseAuditRecords,
  );

  const buildSummaryData = (
    planId: string,
  ): TravelHealthSummaryData | undefined => {
    const plan = allPlans.find((p: any) => p.id === planId);
    if (!plan) return undefined;
    return {
      _type: "travel-health-consultation",
      plan,
      visits: allVisits.filter((v: any) => v.planId === planId),
      doseAuditRecords: allDoseAuditRecords.filter(
        (r: any) => r.planId === planId,
      ),
    };
  };

  useEffect(() => {
    if (isConsultationCompleted) return;
    if (
      travelHealthStep === "plan-check" &&
      travelHealthExistingPlans.length === 0
    ) {
      setTravelHealthStep("tier1-assessment");
    }
  }, [travelHealthStep, travelHealthExistingPlans.length, isConsultationCompleted]);

  // Travel Health treatment plan modal helpers
  const travelHealthRoom = (() => {
    const site = initialLocations.find((loc) => loc.id === activeSiteId);
    return site?.rooms.find((r) =>
      r.services.includes("Travel Health Consultation"),
    );
  })();

  const travelHealthRoomName: string =
    travelHealthRoom?.name ?? "Travel Health Room";

  // Map member id → full name for appointment pharmacist field
  const memberNameMap: Record<string, string> = Object.fromEntries(
    initialMembers.map((m) => [m.id, `${m.forename} ${m.surname}`]),
  );

  // Filter schedulePharmacists to Travel Health qualified members only
  const travelHealthPharmacists = schedulePharmacists.filter((name) =>
    initialMembers.some(
      (m) =>
        `${m.forename} ${m.surname}` === name &&
        m.servicesProvided?.includes("Travel Health"),
    ),
  );

  return (
    <>
      <div className="mt-3">
        {isConsultationCompleted ? (
          <div className="mt-3 space-y-4">
            <div className="rounded-lg border border-green-200 bg-green-50 p-4 space-y-2">
              <p className="text-sm font-semibold text-green-900">
                Travel Health consultation completed
              </p>
              <p className="text-sm text-green-800">
                This consultation has been completed and the summary has been
                saved. View the summary on the Summary tab.
              </p>
            </div>
          </div>
        ) : (
          <>
            {travelHealthStep === "plan-check" &&
              (travelHealthExistingPlans.length === 0 ? null : (
                <div className="mt-3 space-y-4">
                  <div className="rounded-lg border border-blue-200 bg-blue-50 p-4 space-y-3">
                    <p className="text-sm font-semibold text-blue-900">
                      Active travel health plan found
                    </p>
                    <p className="text-sm text-blue-800">
                      This patient has an active travel health plan. Would you
                      like to resume it or start a new consultation?
                    </p>
                  </div>

                  {travelHealthExistingPlans.map((plan) => (
                    <div
                      key={plan.id}
                      className="rounded-lg border border-gray-200 bg-white p-4 space-y-2"
                    >
                      <p className="text-sm font-semibold text-gray-900">
                        {plan.tripDestinations.join(", ")}
                      </p>
                      <p className="text-xs text-gray-500">
                        Departure:{" "}
                        {new Date(plan.departureDate).toLocaleDateString(
                          "en-GB",
                        )}{" "}
                        · Created:{" "}
                        {new Date(plan.createdAt).toLocaleDateString("en-GB")}
                      </p>
                      <Button
                        className="w-full bg-slate-900 hover:bg-slate-800"
                        onClick={() => {
                          setTravelHealthPlanId(plan.id);
                          setShowTravelHealthPlanTimeline(true);
                        }}
                      >
                        Resume this plan
                      </Button>
                    </div>
                  ))}

                  <Button
                    variant="outline"
                    className="w-full"
                    onClick={() => setTravelHealthStep("tier1-assessment")}
                  >
                    Start a new consultation
                  </Button>
                </div>
              ))}

            {travelHealthStep === "tier1-assessment" && (
              <TravelHealthTier1Assessment
                patientId={
                  (patient as any).id ||
                  (patient as any).nhsNumber ||
                  "current-patient"
                }
                patientAge={travelHealthPatientAge}
                patientSex={(patient as any).gender ?? ""}
                siteId={activeSiteId ?? ""}
                questionnaireResponses={travelHealthQuestionnaireResponses}
                initialWeightKg={vitalsWeightKg}
                restoredState={savedSnapshot?.tier1 as Tier1SavedState | undefined}
                onAutoSaveSnapshot={handleTier1AutoSave}
                onComplete={(data) => {
                  setTier1AssessmentData(data);
                  setTravelHealthStep("tier1-selection");
                }}
                onCancel={() => {
                  onClose();
                  setTravelHealthStep("tier1-assessment");
                }}
              />
            )}

            {travelHealthStep === "tier1-selection" && tier1AssessmentData && (
              <TravelHealthTier1Selection
                tier1Data={tier1AssessmentData}
                patientId={
                  (patient as any).id ||
                  (patient as any).nhsNumber ||
                  "current-patient"
                }
                patientAge={travelHealthPatientAge}
                patientSex={(patient as any).gender ?? ""}
                patientDob={(patient as any).dateOfBirth ?? ""}
                siteId={activeSiteId ?? ""}
                isYellowFeverCentre={isYellowFeverCentre}
                yellowFeverCentreNumber={yellowFeverCentreNumber}
                restoredState={savedSnapshot?.tier1Selection as Tier1SelectionSavedState | undefined}
                onAutoSaveSnapshot={handleTier1SelectionAutoSave}
                onComplete={(planId, appointmentSlots, supplyTaskDates) => {
                  setTravelHealthPlanId(planId);
                  setTravelHealthAppointmentSlots(appointmentSlots);
                  console.log("Antimalarial supply task dates:", supplyTaskDates);
                  setShowTravelHealthPlanModal(true);
                }}
                onCancel={() => {
                  onClose();
                  setTravelHealthStep("tier1-assessment");
                }}
              />
            )}

            {travelHealthStep === "tier2-runner" &&
              travelHealthPlanId &&
              travelHealthProductId &&
              travelHealthPgdId && (
                <TravelHealthTier2Runner
                  planId={travelHealthPlanId}
                  productId={travelHealthProductId}
                  pgdId={travelHealthPgdId}
                  consultationId={consultationId || ""}
                  patientAge={travelHealthPatientAge}
                  patientSex={(patient as any).gender ?? ""}
                  patientWeightKg={tier1AssessmentData?.weightKg ?? ""}
                  sitesUsedToday={travelHealthSitesUsedToday}
                  hasAllergyHistory={
                    (tier1AssessmentData?.allergies &&
                      (tier1AssessmentData.allergies.egg ||
                        tier1AssessmentData.allergies.latex ||
                        tier1AssessmentData.allergies.neomycinOrAminoglycoside ||
                        tier1AssessmentData.allergies.formaldehyde ||
                        tier1AssessmentData.allergies.gelatinOrPolygeline ||
                        tier1AssessmentData.allergies.sulphonamides ||
                        tier1AssessmentData.allergies.aMedicine ||
                        tier1AssessmentData.allergies.aFood ||
                        tier1AssessmentData.allergies.aPreviousVaccine ||
                        tier1AssessmentData.allergies.other)) ??
                    false
                  }
                  isUnder19={travelHealthPatientAge < 19}
                  restoredState={savedSnapshot?.tier2 as Tier2SavedState | undefined}
                  onAutoSaveSnapshot={handleTier2AutoSave}
                  onComplete={() => {
                    clearSavedState();
                    onClose(
                      travelHealthPlanId
                        ? buildSummaryData(travelHealthPlanId)
                        : undefined,
                    );
                    setTravelHealthStep("plan-check");
                  }}
                  onCancel={() => {
                    onClose();
                    setTravelHealthStep("plan-check");
                  }}
                  onSiteRecorded={handleTravelHealthSiteRecorded}
                />
              )}

            {showTravelHealthPlanTimeline && travelHealthPlanId && (
              <TravelHealthPlanTimeline
                planId={travelHealthPlanId}
                onOpenTier2={(productId, pgdId) => {
                  setTravelHealthProductId(productId);
                  setTravelHealthPgdId(pgdId);
                  setShowTravelHealthPlanTimeline(false);
                  setTravelHealthStep("tier2-runner");
                }}
                onClose={() => setShowTravelHealthPlanTimeline(false)}
              />
            )}
          </>
        )}
      </div>

      {showTravelHealthPlanModal && (
        <TreatmentPlanModal
          open={showTravelHealthPlanModal}
          onOpenChange={(open) => {
            setShowTravelHealthPlanModal(open);
          }}
          consultationId={consultationId}
          initialFollowUpAppointments={convertSlotsToFollowUpAppointments(
            travelHealthAppointmentSlots,
            memberNameMap,
          )}
          schedulePharmacists={travelHealthPharmacists}
          scheduleRoomNames={[travelHealthRoomName]}
          selectedPatient={patient}
          onSave={async (treatmentPlan) => {
            console.log(
              "[Travel Health] Treatment plan saved:",
              treatmentPlan,
            );

            const plan = allPlans.find((p: any) => p.id === travelHealthPlanId);
            if (plan?.basket?.length > 0 && consultationId) {
              const nowIso = new Date().toISOString();
              const vaccineItems = plan.basket.map((product: any) => ({
                vaccineName: product.productName,
                brandName: "",
                dose: `Dose ${(product.currentDoseNumber || 0) + 1}`,
                schedule: "",
                route: "",
                site: "",
                batchNumber: "",
                expiryDate: "",
                notes: "",
                serviceType: "administration",
              }));
              const draftPayload = {
                payload: JSON.stringify({
                  vaccineItems,
                  timestamp: nowIso,
                }),
                actionName: "CreateVaccineDraft",
                actionNumber: "12",
                isPerformed: true,
                performedAt: nowIso,
                draftStatus: "pending",
              };
              await updateConsultationActions({
                consultationId,
                payload: draftPayload,
              });
              const chatMessage = {
                sender: "success",
                message: `${vaccineItems.length} vaccine draft${vaccineItems.length > 1 ? "s" : ""} created.`,
                actionId: 12,
                details: draftPayload,
              };
              await SaveAIChatMessage({
                ConsultationChat: JSON.stringify([chatMessage]),
                ConsultationId: consultationId,
              });
              setAddPatientAIChatMessage(chatMessage);
            }

            setShowTravelHealthPlanModal(false);
            await onClose(
              travelHealthPlanId
                ? buildSummaryData(travelHealthPlanId)
                : undefined,
            );
            setTravelHealthStep("plan-check");
            setTravelHealthAppointmentSlots([]);
          }}
          isDarkMode={isDarkMode}
        />
      )}
    </>
  );
}

export type { TravelHealthConsultationProps };
